import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import type { TableColumn } from "../types";

interface CustomDataTableProps {
  loading?: boolean;
  title?: string;
  columns?: any;
  data?: any;
  onRowClicked?: (row: any) => void;
  pagination?: boolean;
  pointerOnHover?: boolean;
  highlightOnHover?: boolean;
  total?: number;
  countPerPage?: number;
  dataFetchApi?: (...args: any[]) => void;
  isServerSide?: boolean;
  sortServer?: boolean;
  noDataComponent?: React.ReactNode;
  conditionalRowStyles?: any;
  fixedHeader?: boolean;
  fixedHeaderScrollHeight?: string;
  selectedRetreat?: string | number;
  committeeId?: string | number | null;
  parishValue?: any;
  forCouple?: boolean;
  selectableRows?: boolean;
  selectableRowsNoSelectAll?: boolean;
  onSelectedRowsChange?: (selected: any) => void;
  className?: string;
  [key: string]: any;
}

const CustomDataTable: React.FC<CustomDataTableProps> = ({
  loading = false,
  title = "",
  columns = [],
  data = [],
  onRowClicked,
  pagination = true,
  pointerOnHover = false,
  highlightOnHover = false,
  total,
  countPerPage = 20,
  dataFetchApi = null,
  isServerSide = false,
  sortServer = false,
  noDataComponent = (
    <h5 className="mb-3 mt-2">There are no records to display</h5>
  ),
  conditionalRowStyles,
  fixedHeader = false,
  fixedHeaderScrollHeight = "455px",
  selectedRetreat = "",
  committeeId = null,
  parishValue = null,
  forCouple = false,
  selectableRows = false,
  selectableRowsNoSelectAll = false,
  onSelectedRowsChange = () => { },
}) => {
  let location = useLocation();
  let url = location.pathname;
  const [perPage, setPerPage] = useState<number>(countPerPage);
  let checkPagination = total && total > perPage ? true : false;
  const [pending, setPending] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<Record<string, unknown>>({});
  const [colm, setColm] = useState(columns);

  const prevPageRef = useRef<number | undefined>();
  useEffect(() => {
    if (dataFetchApi && prevPageRef.current !== undefined)
      if (forCouple === true) {
        dataFetchApi(page, perPage, null, sortBy, selectedRetreat, parishValue);
      } else {
        dataFetchApi(
          page,
          perPage,
          null,
          sortBy,
          selectedRetreat,
          "",
          committeeId,
          parishValue
        );
      }

    prevPageRef.current = page;
  }, [page, sortBy, perPage]);

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f8f9fb",
        fontSize: "0.82rem !important",
        fontWeight: "700",
        innerHeight: "20px",
        height: "38px",
      },
    },
    head: {
      style: {
        height: "38px",
      },
    },
    rows: {
      style: {
        height: "auto !important",
        minHeight: "48px !important",
      },
    },
    cells: {
      style: {
        height: "auto !important",
        minHeight: "48px !important",
        verticalAlign: "middle",
        fontSize: "0.82rem",
        lineHeight: "1.5",
        fontWeight: "500",
      },
    },
    table: {
      style: {
        paddingBottom: "0",
      },
    },
  };

  const titleStyles: React.CSSProperties = {
    fontWeight: "700",
    textTransform: "capitalize",
    fontFamily: "Open Sans, sans-serif",
    fontSize: "16px",
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
  };

  const handleSort = (column: any, sortDirection: string) => {
    let direction = sortDirection === "asc" ? 1 : -1;
    let s: Record<string, unknown> = { ...sortBy };
    s.sortColumn = column.id;
    s.sortDirection = direction;
    setSortBy(s);
  };

  return (
    <span className="c-overflow">
      <DataTable
        pointerOnHover={pointerOnHover}
        highlightOnHover={highlightOnHover}
        title={title ? <span style={titleStyles}>{title}</span> : ""}
        columns={colm as any}
        data={data}
        progressPending={loading}
        progressComponent={
          <Spinner
            animation={"border spinner-border" as any}
            style={{ color: "red", margin: "20px" }}
          />
        }
        customStyles={customStyles}
        noDataComponent={noDataComponent}
        pagination={pagination}
        onRowClicked={onRowClicked}
        paginationServer={isServerSide}
        paginationTotalRows={total}
        paginationPerPage={perPage}
        paginationComponentOptions={{
          noRowsPerPage: false,
          selectAllRowsItem: true,
          selectAllRowsItemText: "All",
        }}
        paginationRowsPerPageOptions={[10, 15, 20, 50, 100]}
        onChangeRowsPerPage={(e, page) => handlePerRowsChange(e, page)}
        onChangePage={(page) => setPage(page)}
        sortServer={sortServer}
        onSort={handleSort}
        conditionalRowStyles={conditionalRowStyles}
        selectableRows={selectableRows}
        selectableRowsNoSelectAll={selectableRowsNoSelectAll}
        onSelectedRowsChange={onSelectedRowsChange}
      />
    </span>
  );
};

export default CustomDataTable;
